import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AvanoButtonBarButtonDetails } from '@avano/avano-components';
import { Ability } from '@casl/ability';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AbilityActions, AbilitySites, EnvironmentConfigurationService, NotificationService, ProductTableService } from '../../../services';
import { LoadingInfo, ProductSearchResultDto } from '@scip/api';

@Component({
  selector: 'scip-products-table-tab',
  templateUrl: './products-table-tab.component.html',
  styleUrls: ['./products-table-tab.component.scss']
})
export class ProductsTableTabComponent implements OnInit, OnDestroy {
  @Input() loadingInfo: LoadingInfo;

  @Output() readonly openProductTab: EventEmitter<ProductSearchResultDto[]> = new EventEmitter<ProductSearchResultDto[]>();

  buttonBarButtons: AvanoButtonBarButtonDetails[];

  selectedProducts: ProductSearchResultDto[] = [];

  //Dossier Upload Variables
  showDossierUploadDialog = false;

  showEditDialog = false;
  private productChanged = false;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private productTableService: ProductTableService,
    private ability: Ability,
    private env: EnvironmentConfigurationService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.initializeButtonBarButtons();

    this.productTableService.selectionChanged.pipe(takeUntil(this.destroyed$)).subscribe((currentSelection: ProductSearchResultDto[]) => {
      this.calculateButtonState(currentSelection);
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private initializeButtonBarButtons(): void {
    this.buttonBarButtons = [];

    if (this.ability.can(AbilityActions.update, AbilitySites.submission)) {
      this.buttonBarButtons.push({
        icon: 'icon-content-upload',
        label: 'productsComponent.typescript.uploadDossierButtonText',
        action: () => this.submitDossier(),
        isDisabled: true
      });
    }

    this.buttonBarButtons.push({
      icon: 'icon-content-show-worklist',
      label: 'productsComponent.typescript.goToDetailsButtonText',
      action: () => this.openProductDetails(),
      isDisabled: true
    });

    if (this.ability.can(AbilityActions.update, AbilitySites.product)) {
      this.buttonBarButtons.push({
        icon: 'icon-content-edit',
        label: 'productsComponent.typescript.editSelectedProductButtonText',
        action: () => this.openEditProductDialog(),
        isDisabled: true
      });
    }
  }

  submitDossier(): void {
    const filteredSelection: ProductSearchResultDto[] = [];
    if (this.selectedProducts.length !== 0) {
      for (const product of this.selectedProducts) {
        if (product.lockedForUpdate === true) {
          const interpolParam = { productName: product.name };
          this.notificationService.showInfo('productsComponent.typescript.msg.productNotLoadedInfo', interpolParam);
        } else {
          filteredSelection.push(product);
        }
      }
      if (filteredSelection.length > 0) {
        this.showDossierUploadDialog = true;
      }
    }
  }

  closeDossierUploadDialog(isUploaded: boolean): void {
    // The dossier upload dialog from within the table was closed,
    // Lets save if the uploading was triggered or the dialog was just closed without uploading.
    this.productChanged = isUploaded;
    this.checkForRefreshingTable();

    // Lets hide the dialog
    this.showDossierUploadDialog = false;
  }

  openEditProductDialog(): void {
    this.showEditDialog = true;
  }

  openProductDetails(): void {
    this.openProductTab.emit(this.selectedProducts);

    this.productTableService.setProducts([]); // We opened the details, selection should not persist.
  }

  closeEditProductDialog(isEdited: boolean): void {
    // The edit product dialog was closed, lets save if the products has changed or not.
    this.productChanged = isEdited;
    this.checkForRefreshingTable();

    // Lets hide the dialog
    this.showEditDialog = false;
  }

  private calculateButtonState(currentSelection: ProductSearchResultDto[]): void {
    if (this.buttonBarButtons && currentSelection) {
      const goToDetailsBtn = this.buttonBarButtons.find((element) => element.icon === 'icon-content-show-worklist');
      const openEditDialogBtn = this.buttonBarButtons.find((element) => element.icon === 'icon-content-edit');
      const uploadDossierBtn = this.buttonBarButtons.find((element) => element.icon === 'icon-content-upload');
      this.selectedProducts = currentSelection;
      const isButtonHidden = currentSelection.length === 0;
      this.setHiddenStateButton(goToDetailsBtn, isButtonHidden);
      this.setHiddenStateButton(openEditDialogBtn, isButtonHidden);
      this.setHiddenStateButton(uploadDossierBtn, isButtonHidden);
    }
  }

  private setHiddenStateButton(buttonToUpdate: AvanoButtonBarButtonDetails, isHidden: boolean): void {
    if (buttonToUpdate) {
      buttonToUpdate.isDisabled = isHidden;
    }
  }

  private checkForRefreshingTable(): void {
    // If one or more dossiers has been uploaded or one or more products has been edited, we should reload the list
    // as the content of the list could have changed (e.g. dossier status or article categories...)
    if (this.productChanged) {
      this.productTableService.triggerRefreshProductsTable();
      this.productChanged = false; // Reset it because we refreshed the table
    }
  }
}
